import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Watchlist from "./pages/Watchlist";
import Dashboard from "./pages/Dashboard";
import Calculator from "./pages/Calculator";
import History from "./pages/History";
import ScanAuto from "./pages/ScanAuto";
import Notifications from "./pages/Notifications";
import Backtest from "./pages/Backtest";
import Learn from "./pages/Learn";
import NewsEco from "./pages/NewsEco";
import DCA from "./pages/DCA";
import Subscribe from "./pages/Subscribe";
import SubscribeSuccess from "./pages/SubscribeSuccess";
import TrackRecord from "./pages/TrackRecord";
import AdminData from "./pages/AdminData";
import Admin from "./pages/Admin";
import EmailPreferences from "./pages/EmailPreferences";
import Unsubscribe from "./pages/Unsubscribe";
import AccountSettings from "./pages/AccountSettings";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AffiliateLanding from "./pages/AffiliateLanding";
import Contact from "./pages/Contact";
import CallbackRequest from "./pages/CallbackRequest";
import FAQ from "./pages/FAQ";
import LegalCGU from "./pages/LegalCGU";
import LegalRGPD from "./pages/LegalRGPD";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route - redirects to dashboard if already authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Synchronous hash check: when Emergent OAuth redirects back with #session_id=...
  // we must exchange it BEFORE the router runs any /auth/me protected checks.
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Layout>
            <Landing />
          </Layout>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Layout>
              <Login />
            </Layout>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Layout>
              <Register />
            </Layout>
          </PublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <Layout>
            <Watchlist />
          </Layout>
        }
      />
      <Route
        path="/analysis"
        element={
          <Layout>
            <Dashboard />
          </Layout>
        }
      />
      <Route
        path="/calculator"
        element={
          <Layout>
            <Calculator />
          </Layout>
        }
      />
      <Route
        path="/history"
        element={
          <Layout>
            <History />
          </Layout>
        }
      />
      <Route
        path="/scan-auto"
        element={
          <Layout>
            <ScanAuto />
          </Layout>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Layout>
              <Notifications />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/backtest"
        element={
          <Layout>
            <Backtest />
          </Layout>
        }
      />
      <Route
        path="/learn"
        element={
          <Layout>
            <Learn />
          </Layout>
        }
      />
      <Route
        path="/news-eco"
        element={
          <Layout>
            <NewsEco />
          </Layout>
        }
      />
      <Route
        path="/dca"
        element={
          <Layout>
            <DCA />
          </Layout>
        }
      />
      <Route
        path="/subscribe"
        element={
          <Layout>
            <Subscribe />
          </Layout>
        }
      />
      <Route
        path="/subscribe/success"
        element={
          <Layout>
            <SubscribeSuccess />
          </Layout>
        }
      />
      <Route
        path="/track-record"
        element={
          <Layout>
            <TrackRecord />
          </Layout>
        }
      />
      <Route
        path="/rappel"
        element={
          <Layout>
            <CallbackRequest />
          </Layout>
        }
      />
      <Route
        path="/callback"
        element={
          <Layout>
            <CallbackRequest />
          </Layout>
        }
      />
      <Route
        path="/admin/data"
        element={
          <Layout>
            <AdminData />
          </Layout>
        }
      />
      <Route
        path="/admin"
        element={
          <Layout>
            <Admin />
          </Layout>
        }
      />
      <Route
        path="/account/email-preferences"
        element={
          <ProtectedRoute>
            <Layout>
              <EmailPreferences />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <AccountSettings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <Layout>
              <ForgotPassword />
            </Layout>
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Layout>
            <ResetPassword />
          </Layout>
        }
      />
      <Route
        path="/unsubscribe"
        element={<Unsubscribe />}
      />
      <Route path="/r/:code" element={<Layout><AffiliateLanding /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/faq" element={<Layout><FAQ /></Layout>} />
      <Route path="/legal/cgu" element={<Layout><LegalCGU /></Layout>} />
      <Route path="/legal/rgpd" element={<Layout><LegalRGPD /></Layout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Toaster with theme awareness
const ThemedToaster = () => {
  const { isDark } = useTheme();
  
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        style: isDark ? {
          background: "hsl(220 10% 7%)",
          border: "1px solid rgba(255,255,255,0.1)",
          color: "white",
        } : {
          background: "white",
          border: "1px solid rgba(0,0,0,0.1)",
          color: "hsl(220 15% 15%)",
        },
      }}
    />
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <AppRoutes />
            <PWAInstallPrompt />
            <ThemedToaster />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
